import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AppManager.css';

import backIcon from '../../assets/backspace-icon.png';
import api from '../../api';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function AppManager({ user }) {
  const navigate = useNavigate();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [reviewStatus, setReviewStatus] = useState('pending');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });

  const canLoadMore = useMemo(() => {
    return pagination.page < pagination.pages;
  }, [pagination]);

  const fetchApps = async ({ page = 1, append = false, status = reviewStatus, query = search } = {}) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      setError('');

      const { data } = await api.get('/admin/apps/review-queue', {
        params: {
          page,
          limit: 20,
          reviewStatus: status,
          search: query.trim(),
        },
      });

      const incomingApps = data?.apps || [];
      const incomingPagination = data?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 1,
      };

      setApps((prev) => (append ? [...prev, ...incomingApps] : incomingApps));
      setPagination(incomingPagination);
    } catch (err) {
      console.error('Failed to fetch app review queue:', err);
      setError(err?.response?.data?.message || 'Failed to load apps');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchApps({ page: 1, append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchApps({ page: 1, append: false, status: reviewStatus, query: search });
  };

  const handleStatusChange = (nextStatus) => {
    setReviewStatus(nextStatus);
    fetchApps({ page: 1, append: false, status: nextStatus, query: search });
  };

  const handleLoadMore = () => {
    if (!canLoadMore || loadingMore) return;
    fetchApps({
      page: pagination.page + 1,
      append: true,
      status: reviewStatus,
      query: search,
    });
  };

  if (!user || user.userType !== 'admin') {
    return <div className="admin-apps-denied">Access denied.</div>;
  }

  return (
    <div className="admin-apps-container">
      <div className="admin-apps-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="admin-apps-back-icon"
          onClick={() => navigate('/admin')}
        />
        <h2>Apps Review</h2>
      </div>

      <div className="admin-apps-controls">
        <form className="admin-apps-search-row" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search by name, username, or domain"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="admin-apps-search-input"
          />
          <button type="submit" className="admin-apps-search-btn">
            Search
          </button>
        </form>

        <div className="admin-apps-filter-row">
          <button
            className={`admin-apps-filter-btn ${reviewStatus === 'pending' ? 'active' : ''}`}
            onClick={() => handleStatusChange('pending')}
          >
            Pending
          </button>
          <button
            className={`admin-apps-filter-btn ${reviewStatus === 'rejected' ? 'active' : ''}`}
            onClick={() => handleStatusChange('rejected')}
          >
            Rejected
          </button>
          <button
            className={`admin-apps-filter-btn ${reviewStatus === 'all' ? 'active' : ''}`}
            onClick={() => handleStatusChange('all')}
          >
            All
          </button>
        </div>
      </div>

      {loading ? (
        <div className="admin-apps-state">Loading apps...</div>
      ) : error ? (
        <div className="admin-apps-state admin-apps-error">{error}</div>
      ) : apps.length === 0 ? (
        <div className="admin-apps-state">No apps found.</div>
      ) : (
        <>
          <div className="admin-apps-list">
            {apps.map((app) => {
              const owner = app.primaryOwner || {};
              return (
                <div
                  key={app._id}
                  className="admin-app-card"
                  onClick={() => navigate(`/admin/apps/${app._id}`)}
                >
                  <div className="admin-app-card-top">
                    <img
                      src={app.profilePic || '/profile/placeholder-logo.png'}
                      alt={app.name}
                      className="admin-app-card-avatar"
                    />

                    <div className="admin-app-card-main">
                      <div className="admin-app-card-name-row">
                        <h3>{app.name}</h3>
                        <span className={`admin-app-review-pill ${app.reviewStatus || 'pending'}`}>
                          {app.reviewStatus || 'pending'}
                        </span>
                      </div>

                      <div className="admin-app-card-subtext">@{app.username}</div>
                      <div className="admin-app-card-subtext">{app.domain || 'No active domain'}</div>
                      <div className="admin-app-card-subtext">
                        Owner: {owner.name || 'Unknown'} {owner.username ? `(@${owner.username})` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="admin-app-card-meta">
                    <span>Category: {app.category || '—'}</span>
                    <span>Verified: {app.domainVerificationStatus || '—'}</span>
                    <span>Requested: {formatDate(app.reviewRequestedAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {canLoadMore && (
            <button
              className="admin-apps-load-more"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default AppManager;
